/*
 * Getting a run ready: the packages its imports need, and the clock it runs against.
 *
 * Pure, like bind.ts, so it is tested against the real Pyodide in Node
 * (test/sandbox-prepare.test.ts); the worker and the session call it.
 */

/** The slice of the Pyodide API installing needs. */
export interface PyodideInstaller {
  loadPackagesFromImports(
    code: string,
    options?: { messageCallback?: (m: string) => void; errorCallback?: (m: string) => void },
  ): Promise<unknown>;
  runPythonAsync(code: string): Promise<unknown>;
  /** Package name → where it came from, for every package installed so far. */
  loadedPackages: Record<string, unknown>;
}

/**
 * Packages that come with another, because the other imports them from inside
 * itself where reading the code's imports cannot see it.
 *
 * pandas writes Excel through xlsxwriter, reads it through openpyxl and renders
 * `to_markdown()` through tabulate — and code that calls `df.to_excel(...)`
 * never writes `import xlsxwriter`. On a bench, exactly that run failed and the
 * model concluded the sandbox could not produce a workbook. They are small and
 * pure Python (~0.5 MB together), so they come with pandas rather than wait for
 * an import that will never be written. `tzdata` rides along for the same
 * reason: `tz_convert('Europe/Berlin')` reads the zone from it, and no code ever
 * imports it by name. A companion that cannot be installed
 * is reported and does not fail the run: it is pandas the code asked for.
 */
const COMPANIONS: Record<string, string> = { pandas: 'import xlsxwriter\nimport openpyxl\nimport tabulate\nimport tzdata' };

/**
 * Import, ahead of the code, every module its import statements name.
 *
 * INSTALLING IS NOT IMPORTING. A package is installed when its files are in
 * place; the first `import statsmodels.api` then runs its whole module tree,
 * which takes seconds — measured on a bench, a run whose packages had been
 * installed still spent its three-second deadline importing them. That cost is
 * the sandbox's, not the code's, so it is paid here, while preparing. A module
 * that fails to import is left alone: the code imports it again and raises the
 * error itself, with its line. Nothing is left in the run's globals.
 */
const PREIMPORT_PY = (code: string) => `
def _vitesse_preimport(_code):
    import importlib
    from pyodide.code import find_imports
    # Code that does not parse names no imports here, and raises its own SyntaxError when it runs.
    for _name in find_imports(_code):
        try:
            importlib.import_module(_name)
        except Exception:
            pass
_vitesse_preimport(${JSON.stringify(code)})
del _vitesse_preimport
`;

/**
 * Install what the code imports, before it runs.
 *
 * IMPORTS ARE THE DECLARATION. The model no longer names a package tier: it
 * writes `import scipy` and scipy is there, which removes a choice it got wrong
 * (a run that drew a chart under the default tier failed for want of
 * matplotlib) and installs nothing the code does not use. Pyodide reads the
 * imports and resolves them against the sandbox's lockfile, which lists only
 * what ships, so an import outside the catalog installs nothing and fails in
 * the code as a plain ModuleNotFoundError the model can read.
 *
 * A run with datasets needs pandas whatever the code imports: the datasets are
 * bound as DataFrames before the code runs.
 *
 * Installing DOES NOT THROW when a package fails — Pyodide reports it to a
 * callback and resolves — so the failures are collected and thrown here, naming
 * what could not be loaded. Code that does not parse installs nothing and is
 * left to run, so the model gets the SyntaxError with its line.
 *
 * Then the modules are imported once, here (see PREIMPORT_PY), so the code's
 * deadline is spent on the code.
 */
export async function installFor(
  py: PyodideInstaller,
  code: string,
  opts: { datasets: boolean; onProgress?: (text: string) => void },
): Promise<void> {
  const failures: string[] = [];
  const options = {
    messageCallback: (m: string) => opts.onProgress?.(m),
    errorCallback: (m: string) => {
      failures.push(m);
      opts.onProgress?.('install error: ' + m);
    },
  };
  if (opts.datasets) await py.loadPackagesFromImports('import pandas', options);
  await py.loadPackagesFromImports(code, options);
  if (failures.length) throw new Error(`the sandbox could not load a package this code imports: ${failures.join(' ')}`);
  for (const [pkg, imports] of Object.entries(COMPANIONS)) {
    if (!(pkg in py.loadedPackages)) continue;
    await py.loadPackagesFromImports(imports, {
      messageCallback: (m: string) => opts.onProgress?.(m),
      errorCallback: (m: string) => opts.onProgress?.(`install error (${pkg} companion, not fatal): ${m}`),
    });
  }
  await py.runPythonAsync(PREIMPORT_PY(code));
}

/**
 * Install the house helpers — the `vitesse` module — and import it into the
 * interpreter's globals, once, when it boots.
 *
 * PRELOADED, so the right output is the default rather than something the model
 * has to remember: `vitesse.excel(...)` works without an import, and `import
 * vitesse` works too, because the module is a real file on the path rather than a
 * global someone conjured. Written to site-packages, beside every other library,
 * and NOT the working directory: anything written there is handed to the person
 * as a produced file (outputs.ts).
 *
 * The module imports nothing heavy at its top, so booting pays only for its own
 * few hundred lines. The source arrives as a string literal — JSON is a valid
 * Python string literal for everything JSON.stringify emits — so this needs no
 * filesystem API and runs the same against the real interpreter in a test.
 */
export async function installHelpers(py: Pick<PyodideInstaller, 'runPythonAsync'>, source: string): Promise<void> {
  await py.runPythonAsync(
    `def _vitesse_install(src):\n` +
      `    import os, site\n` +
      `    with open(os.path.join(site.getsitepackages()[0], 'vitesse.py'), 'w') as f:\n` +
      `        f.write(src)\n` +
      `_vitesse_install(${JSON.stringify(source)})\n` +
      `del _vitesse_install\n` +
      `import vitesse\n`,
  );
}

/**
 * Teach the interpreter what to say when code reaches a shipped package that
 * was not installed — once, when it boots.
 *
 * Packages install when the code imports them BY NAME. Code that reaches one
 * another way — through a library that imports it inside itself, or through
 * `importlib.import_module` — finds it missing, and Pyodide's own note then
 * says "install it with micropip": advice a sandbox with no network cannot
 * take, and on a bench the model took it as proof the library did not exist.
 *
 * A finder at the END of `sys.meta_path` is asked only after every real finder
 * has failed, so an installed package never meets it. For a top-level name the
 * sandbox ships, it raises the error with the sentence that fixes it, and sets
 * the flag Pyodide checks before adding its own note. A missing submodule of an
 * installed package (`scipy.nope`) is not its business and gets Python's
 * ordinary error. A library that catches the error and words its own (pandas
 * plotting: "matplotlib is required") keeps its wording.
 */
export async function installImportHint(py: Pick<PyodideInstaller, 'runPythonAsync'>, lockJson: string): Promise<void> {
  const lock = JSON.parse(lockJson) as { packages: Record<string, { imports?: string[] }> };
  const shipped = [...new Set(Object.values(lock.packages).flatMap((p) => p.imports ?? []))].sort();
  await py.runPythonAsync(`
def _vitesse_hint(_shipped):
    import sys, importlib.abc
    try:
        from _pyodide._importhook import PYODIDE_ADDED_NOTE as flag
    except Exception:
        flag = '_PYODIDE_ADDED_NOTE'

    class ShippedButNotImported(importlib.abc.MetaPathFinder):
        def find_spec(self, name, path=None, target=None):
            if '.' in name or name not in _shipped:
                return None
            e = ModuleNotFoundError(
                f"No module named '{name}'. {name} ships with this sandbox and is installed when the code"
                f" imports it by name, but here it was reached indirectly (through another library, or"
                f" importlib). Add 'import {name}' at the top of the code and run it again.",
                name=name,
            )
            setattr(e, flag, True)
            raise e

    sys.meta_path.append(ShippedButNotImported())
_vitesse_hint(frozenset(${JSON.stringify(shipped)}))
del _vitesse_hint
`);
}

/** Which part of a run a deadline stopped. */
export type Phase = 'prepare' | 'code';

/**
 * A run's two clocks: one for getting ready, one for the code.
 *
 * TIMED APART because they are different costs. `timeoutSeconds` is what the
 * model asked its CODE to be allowed; installing scipy the first time, or
 * importing pandas cold to bind a dataset, is the sandbox's own cost and
 * nothing the code can shorten. Counted together, a cold first run spent its
 * thirty seconds on `import pandas` (27 s on a bench), was killed, and the
 * interpreter it had just warmed died with it — so the next run was cold
 * again. Preparing gets its own, much longer bound, which is there only to
 * stop a hung install from holding a run open forever.
 */
export function twoPhaseDeadline(
  opts: { prepareMs: number; codeMs: number; onExpire: (phase: Phase) => void },
  timers: { set: (fn: () => void, ms: number) => unknown; clear: (t: unknown) => void } = {
    set: (fn, ms) => setTimeout(fn, ms),
    clear: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
  },
): { codeStarted(): void; clear(): void } {
  let phase: Phase = 'prepare';
  let timer: unknown = timers.set(() => opts.onExpire('prepare'), opts.prepareMs);
  return {
    codeStarted() {
      if (phase === 'code') return;
      phase = 'code';
      timers.clear(timer);
      timer = timers.set(() => opts.onExpire('code'), opts.codeMs);
    },
    clear() {
      timers.clear(timer);
    },
  };
}

/** How long preparing a run may take: installing the heaviest packages cold, with room. */
export const PREPARE_LIMIT_MS = 180_000;
